import { useMemo, useState } from 'react'
import { Check, Copy, Loader2 } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  getTemplateVariableName,
  toTemplateVariableSyntax,
} from '@/lib/template-variable-tokens'
import {
  formatVariableLabel,
  getTemplateVariableGroupConfig,
  getTemplateVariableParentType,
  templateVariableGroupIndex,
} from '@/lib/template-variable-groups'
import type { TemplateVariableEntityBinding } from '@/lib/email-template-entity'

export interface TemplateVariableOption {
  name: string
  label: string
  description: string
  parentType: string
  syntax: string
}

interface TemplateVariablePickerProps {
  variables: TemplateVariableOption[]
  isLoading?: boolean
  title?: string
  helperText?: string
  emptyMessage?: string
  className?: string
  onInsert: (variable: TemplateVariableOption) => void
}

/**
 * Variable names shown here come in two shapes: nested dot-paths
 * (`{{ .entity.contact.first_name }}`), the current authoring vocabulary
 * served by `GetWorkflowAuthoringSchema`, and flat PascalCase names
 * (`{{.CustomerFirstName}}`) — pre-migration rows, plus the identically-named
 * ad-hoc vocabulary `system_template_overrides.go` independently builds per
 * send site for system templates (a separate, unrelated engine). Every
 * pattern here allows dots, and the shared token/grouping helpers below
 * handle both shapes. Matching only `[A-Za-z0-9_]` made nested-path templates
 * invisible to the picker, the preview and the used-variable extraction —
 * which is how a workflow template's variable list got silently emptied on
 * save.
 */

const systemVariableDescriptions: Record<string, string> = {
  CustomerName: "The customer's display name",
  CustomerEmail: "The customer's email address",
  OrganizationName: "Your organization's name",
  EventDate: 'The event date',
  EventTime: 'The event time',
  EventLocation: 'The event location',
  GuestCount: 'The expected guest count',
  QuoteTotal: 'The quote total',
  InvoiceNumber: 'The invoice number',
  InvoiceTotal: 'The invoice total',
  DueDate: 'The invoice due date',
  PaymentAmount: 'The payment amount',
  PaymentDate: 'The payment date',
  FailureReason: 'The payment failure reason',
  RefundAmount: 'The refund amount',
  'signer.name': 'The name that signs the email body',
  'portal.url': 'A tracked customer link to this lead or event portal',
}

/**
 * `binding` is the optional advisory entity binding (from the template's
 * category — see lib/email-template-entity.ts). A path the bound entity ships
 * takes that entity's server-provided label, which is the only way to tell
 * "Quote Total" from "Invoice Total" for the paths that collide across
 * entities. Every other path — including one the bound entity doesn't ship —
 * keeps the heuristic label and stays in the list.
 */
export function buildSystemTemplateVariableOptions(
  variables: string[],
  binding?: TemplateVariableEntityBinding
): TemplateVariableOption[] {
  return Array.from(new Set(variables.map((variable) => toTemplateVariableSyntax(variable))))
    .filter((syntax) => syntax !== '{{.}}')
    .map((syntax) => {
      const name = getTemplateVariableName(syntax)
      const inEntity = binding?.paths.has(name) === true
      const label = (inEntity ? binding?.labels.get(name) : undefined) ?? formatVariableLabel(name)
      // The heuristic wins where it can classify (contact/org/trigger paths
      // read the same under any entity); the entity's own group only fills the
      // gap where a bare path can't be told apart — `entity.total`, `entity.status`.
      const heuristicType = getTemplateVariableParentType(name)
      const parentType =
        inEntity && binding && heuristicType === 'General' ? binding.group : heuristicType

      return {
        name,
        label,
        description: systemVariableDescriptions[name] ?? `Template key for ${label.toLowerCase()}`,
        parentType,
        syntax,
      }
    })
    .sort((a, b) => {
      const groupDelta =
        templateVariableGroupIndex(a.parentType) - templateVariableGroupIndex(b.parentType)
      if (groupDelta !== 0) return groupDelta
      return a.label.localeCompare(b.label)
    })
}

export function TemplateVariablePicker({
  variables,
  isLoading = false,
  title = 'Available Variables',
  helperText = 'Click a variable to insert it, or use the icon to copy it.',
  emptyMessage = 'No variables are available for this template.',
  className,
  onInsert,
}: TemplateVariablePickerProps) {
  const [copiedVariable, setCopiedVariable] = useState<string | null>(null)

  const groupedVariables = useMemo(() => {
    const groups = variables.reduce<Record<string, TemplateVariableOption[]>>((acc, variable) => {
      const parentType = variable.parentType || 'General'
      if (!acc[parentType]) {
        acc[parentType] = []
      }
      acc[parentType].push(variable)
      return acc
    }, {})

    return Object.entries(groups).sort(
      ([a], [b]) => templateVariableGroupIndex(a) - templateVariableGroupIndex(b)
    )
  }, [variables])

  const copyVariable = async (syntax: string) => {
    try {
      await navigator.clipboard.writeText(syntax)
      setCopiedVariable(syntax)
      setTimeout(() => setCopiedVariable(null), 2000)
    } catch {
      setCopiedVariable(null)
    }
  }

  return (
    <div className={cn('space-y-2 rounded-lg border bg-muted/30 p-3', className)}>
      <div className="flex items-center justify-between gap-3">
        <Label className="font-financial text-xs uppercase tracking-[0.2em] text-dim">
          {title}
        </Label>
        <span className="text-right text-xs text-muted-foreground">{helperText}</span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : groupedVariables.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {groupedVariables.map(([parentType, groupVariables]) => {
            const config = getTemplateVariableGroupConfig(parentType)
            const Icon = config.icon

            return (
              <div key={parentType} className="flex flex-wrap items-center gap-1.5">
                <div className="flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">
                    {config.label}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {groupVariables.map((variable) => (
                    <div
                      key={`${variable.parentType}-${variable.name}`}
                      className={cn(
                        'inline-flex h-6 items-center overflow-hidden rounded-md border text-xs font-medium transition-all hover:shadow-sm',
                        config.color
                      )}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="h-full px-2 outline-none transition-colors hover:bg-background/40 focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={() => onInsert(variable)}
                          >
                            {variable.label}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[220px]">
                          <p className="text-xs">{variable.description}</p>
                          <code className="mt-1 block text-xs opacity-70">{variable.syntax}</code>
                        </TooltipContent>
                      </Tooltip>
                      <button
                        type="button"
                        className="flex h-full items-center border-l border-current/20 px-1.5 opacity-70 outline-none transition-opacity hover:bg-background/40 hover:opacity-100 focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label={`Copy ${variable.label}`}
                        onClick={() => void copyVariable(variable.syntax)}
                      >
                        {copiedVariable === variable.syntax ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
